import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, Phone } from "lucide-react";
import { PageShell, SubpageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { formatDate } from "@/data/posts";
import { useNiches, usePosts, useServices } from "@/hooks/useContent";
import { useSeo } from "@/hooks/useSeo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const CALENDLY = "https://calendly.com/abbie-browne";
const eyebrow = "text-xs uppercase tracking-[0.3em] text-[#0090D0] font-medium mb-5";
const h2 = "font-serif font-semibold tracking-tight text-[#103080] text-3xl sm:text-4xl leading-tight";

const BookCall = ({ testid, light = false }) => (
  <a
    href={CALENDLY}
    target="_blank"
    rel="noreferrer"
    data-testid={testid}
    className={`group inline-flex items-center gap-2 rounded-full text-sm font-medium px-7 py-3.5 transition-colors duration-300 ${light ? "bg-white text-[#103080] hover:bg-[#40C0E0] hover:text-[#103080]" : "bg-[#103080] text-white hover:bg-[#0090D0]"}`}
  >
    Book a free discovery call
    <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </a>
);

const Included = ({ service }) => (
  <section className="py-20 sm:py-28 bg-[#FDFBF7]" data-testid="service-included">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <p className={eyebrow}>{service.includedTitle || "What's included"}</p>
        <h2 className={`${h2} max-w-2xl`}>What you get with {service.title.toLowerCase()} at Rosevale.</h2>
      </Reveal>
      <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {service.included.map((it, i) => (
          <Reveal key={it.title} delay={i * 0.05}>
            <div className="h-full rounded-2xl bg-white border border-[#103080]/10 p-7 lift-shadow" data-testid={`included-${i}`}>
              <span className="text-[11px] uppercase tracking-[0.25em] font-medium" style={{ color: service.accent }}>{String(i + 1).padStart(2, "0")}</span>
              <h3 className="font-serif font-semibold text-xl text-[#103080] leading-snug mt-3">{it.title}</h3>
              <p className="text-sm sm:text-[15px] text-[#0F172A]/65 leading-relaxed mt-3">{it.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ForAndSteps = ({ service }) => (
  <section className="py-20 sm:py-28 bg-[#F5F2EB] border-y border-[#103080]/10">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
      <Reveal className="lg:col-span-5">
        <p className={eyebrow}>{service.forTitle || "Who it's for"}</p>
        <h2 className={h2}>Sound familiar?</h2>
        <ul className="mt-8 space-y-4" data-testid="service-for-list">
          {service.forList.map((t) => (
            <li key={t} className="flex gap-3 text-sm sm:text-base text-[#0F172A]/75 leading-relaxed">
              <span className="shrink-0 mt-0.5 w-6 h-6 rounded-full bg-white border border-[#103080]/10 flex items-center justify-center" style={{ color: service.accent }}>
                <Check size={14} strokeWidth={2.5} />
              </span>
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </Reveal>
      <div className="lg:col-span-7">
        <Reveal>
          <p className={eyebrow}>How it works with us</p>
          <h2 className={h2}>Three steps. No hard sell.</h2>
        </Reveal>
        <ol className="mt-8 space-y-4" data-testid="service-steps">
          {service.steps.map((st, i) => (
            <Reveal key={st.title} delay={i * 0.08}>
              <li className="flex gap-5 rounded-2xl bg-white border border-[#103080]/10 p-6 sm:p-7">
                <span className="shrink-0 w-11 h-11 rounded-full bg-[#103080] text-white font-serif font-semibold text-lg flex items-center justify-center">{i + 1}</span>
                <div>
                  <h3 className="font-serif font-semibold text-xl text-[#103080] leading-snug">{st.title}</h3>
                  <p className="text-sm sm:text-[15px] text-[#0F172A]/65 leading-relaxed mt-2">{st.text}</p>
                </div>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </div>
  </section>
);

const Faqs = ({ service }) => (
  <section className="py-20 sm:py-28 bg-[#FDFBF7]" data-testid="service-faqs">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
      <Reveal className="lg:col-span-4">
        <p className={eyebrow}>Good questions</p>
        <h2 className={h2}>Things people ask us about {service.title.toLowerCase()}.</h2>
        <p className="mt-5 text-[#0F172A]/65 leading-relaxed">Not covered here? Ask Rosie in the corner, or just give us a ring.</p>
        <a href="tel:01173210360" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#103080] hover:text-[#0090D0] transition-colors duration-300" data-testid="service-phone-link">
          <Phone size={15} /> 0117 321 0360
        </a>
      </Reveal>
      <div className="lg:col-span-8">
        <Accordion type="single" collapsible className="rounded-2xl bg-white border border-[#103080]/10 px-6 sm:px-8">
          {service.faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} className="border-[#103080]/10" data-testid={`faq-${i}`}>
              <AccordionTrigger className="text-left font-serif font-semibold text-[#103080] text-lg sm:text-xl py-5 hover:no-underline hover:text-[#0090D0]">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm sm:text-base text-[#0F172A]/70 leading-relaxed pb-6">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </section>
);

const Related = ({ niches, posts }) => (
  (niches.length > 0 || posts.length > 0) && (
    <section className="py-20 sm:py-28 bg-[#F5F2EB] border-t border-[#103080]/10" data-testid="service-related">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
        {niches.length > 0 && (
          <div>
            <p className={eyebrow}>Free guides from the Toolkit</p>
            <div className="space-y-3">
              {niches.map((n) => (
                <Link key={n.slug} to={`/toolkit/${n.slug}`} data-testid={`related-niche-${n.slug}`} className="group flex items-center gap-4 rounded-2xl bg-white border border-[#103080]/10 p-4 sm:p-5 lift-shadow">
                  <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: `${n.accent}1a`, color: n.accent }}><n.icon size={20} /></span>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-serif font-semibold text-[#103080] text-lg leading-snug group-hover:text-[#0090D0] transition-colors duration-300">{n.name}</h3>
                    <p className="text-sm text-[#0F172A]/55 truncate">{n.short}</p>
                  </div>
                  <ArrowRight size={18} className="text-[#103080]/30 group-hover:text-[#0090D0] group-hover:translate-x-1 transition-all duration-300" />
                </Link>
              ))}
            </div>
          </div>
        )}
        {posts.length > 0 && (
          <div>
            <p className={eyebrow}>Related reading</p>
            <div className="space-y-3">
              {posts.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`related-post-${p.slug}`} className="group flex gap-5 rounded-2xl bg-white border border-[#103080]/10 p-4 lift-shadow">
                  <img src={p.image} alt="" className="w-24 h-24 sm:w-28 sm:h-28 object-cover rounded-xl shrink-0" />
                  <div className="min-w-0 py-1">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-[#0F172A]/45">{formatDate(p.date)} · {p.readTime}</p>
                    <h3 className="font-serif font-semibold text-[#103080] text-lg leading-snug mt-2 group-hover:text-[#0090D0] transition-colors duration-300">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  )
);

export default function ServicePage() {
  const { slug } = useParams();
  const SERVICES = useServices();
  const NICHES = useNiches();
  const POSTS = usePosts();
  const service = SERVICES.find((s) => s.slug === slug);

  useSeo({
    title: service?.seoTitle || "Rosevale Accountants",
    description: service?.seoDescription || "",
    path: `/services/${slug}`,
  });

  if (!service) return SERVICES.loaded ? <Navigate to="/#services" replace /> : null;

  const niches = (service.relatedNiches || []).map((s) => NICHES.find((n) => n.slug === s)).filter(Boolean);
  const posts = (service.relatedPosts || []).map((s) => POSTS.find((p) => p.slug === s)).filter(Boolean);
  const others = SERVICES.filter((s) => s.slug !== service.slug);
  const Icon = service.icon;

  return (
    <PageShell testid={`service-page-${service.slug}`}>
      <SubpageHero
        eyebrow={`Our services · ${service.title}`}
        title={service.heroLine}
        lead={service.intro}
        crumbs={[{ label: "Services", to: "/#services" }, { label: service.title }]}
      >
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <BookCall testid="service-book-call-button" />
          <span className="inline-flex items-center gap-2 text-sm text-[#0F172A]/55">
            <Icon size={16} style={{ color: service.accent }} /> Fixed fees · In person at the Science Park or by video
          </span>
        </div>
      </SubpageHero>

      <Included service={service} />
      <ForAndSteps service={service} />
      <Faqs service={service} />

      <section className="py-16 sm:py-20 bg-[#103080]" data-testid="service-cta">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#40C0E0] font-medium mb-4">Next step</p>
            <h2 className="font-serif font-semibold tracking-tight text-white text-3xl sm:text-4xl leading-tight max-w-2xl">
              Thirty minutes, a coffee, and a straight answer on whether we can help.
            </h2>
          </div>
          <BookCall testid="service-cta-book-call" light />
        </div>
      </section>

      <Related niches={niches} posts={posts} />

      <section className="py-14 bg-[#FDFBF7] border-t border-[#103080]/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[#0F172A]/45 font-medium mb-5">Other services</p>
          <div className="flex flex-wrap gap-3">
            {others.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`other-service-${s.slug}`} className="inline-flex items-center gap-2 rounded-full bg-white border border-[#103080]/10 px-5 py-2.5 text-sm font-medium text-[#103080] hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-300">
                <s.icon size={15} /> {s.title}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
